import Button from '@ui/Button'
import { IconShield } from '@ui/icons'
import type { MultiFactorResolver } from 'firebase/auth'
import { type FormEvent, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  IdpError,
  type IdpErrorCode,
  resolveTotp,
  type SignInMethod,
  signOutIdp,
  startSession,
} from '@/auth/idp'
import OneTimeCodeField from '@/components/OneTimeCodeField'

import LoginCard from './LoginCard'

type Props = {
  resolver: MultiFactorResolver
  method: SignInMethod
  email?: string
  /** Abandons the half-finished sign-in and shows the sign-in card again. */
  onBack: () => void
  onNoAccess: (email: string) => void
}

/**
 * The second step of any sign-in (password or Google) when 2FA is on. It owns
 * the whole card: nothing about other methods, one job.
 */
const TotpCard = ({ resolver, method, email, onBack, onNoAccess }: Props) => {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const user = await resolveTotp(resolver, code.trim())
      window.location.assign(await startSession(user))
    } catch (err) {
      if (err instanceof IdpError && err.code === 'no-access') {
        onNoAccess(err.email ?? '')
        return
      }
      setError(err instanceof IdpError ? err.code : 'unknown')
      setBusy(false)
    }
  }

  const back = async () => {
    await signOutIdp()
    onBack()
  }

  return (
    <LoginCard title={t('auth.totp.title')}>
      <form key="totp" onSubmit={onSubmit} className="flex flex-col gap-4">
        <span
          className="w-12 h-12 rounded-full grid place-items-center bg-green-100 text-green-800"
          aria-hidden="true"
        >
          <IconShield size={22} />
        </span>
        <p className="text-sm text-ink-700">
          {email ? (
            <Trans
              i18nKey={
                method === 'google'
                  ? 'auth.totp.intro-provider'
                  : 'auth.totp.intro-password'
              }
              values={{ email, provider: 'Google' }}
              components={{ strong: <strong /> }}
            />
          ) : (
            t('auth.totp.intro-no-email')
          )}
        </p>
        <OneTimeCodeField value={code} onChange={setCode} />
        {error && (
          <p role="alert" className="text-sm text-flag">
            {error === 'mfa-expired'
              ? t('auth.totp.expired')
              : t(`auth.password.errors.${error}` as const)}
          </p>
        )}
        <Button type="submit" fullWidth disabled={busy || code.length < 6}>
          {busy ? t('auth.logging-in') : t('auth.password.verify')}
        </Button>
        <p className="text-sm text-ink-500">{t('auth.totp.lost-device')}</p>
        <Button variant="secondary" fullWidth disabled={busy} onClick={back}>
          {t('auth.password.back-to-sign-in')}
        </Button>
      </form>
    </LoginCard>
  )
}

export default TotpCard
